/**
 * Health Data Watcher Service
 * Platform-agnostic service for monitoring health data changes and auto-updating challenges
 */

import { HealthService, ChallengeService, FirebaseService } from '../index';
import { HealthSummary, Challenge, ChallengeParticipation, ApiResponse } from '../types';

export interface HealthDataChange {
    type: 'steps' | 'distance' | 'calories' | 'activeMinutes';
    value: number;
    timestamp: Date;
    userId: string;
}

export interface WatcherConfig {
    enabled: boolean;
    syncInterval: number; // minutes
    autoUpdateChallenges: boolean;
    notificationReminders: boolean;
    reminderFrequency: 'daily' | 'twice_daily' | 'custom';
    reminderTimes: string[]; // HH:mm format
}

export interface WatcherStatus {
    isActive: boolean;
    lastSync: Date | null;
    lastHealthUpdate: Date | null;
    activeChallenges: number;
    pendingUpdates: number;
    errors: string[];
}

export class HealthDataWatcher {
    private healthService: HealthService;
    private challengeService: ChallengeService;
    private firebaseService: FirebaseService;
    private config: WatcherConfig;
    private status: WatcherStatus;
    private isWatching = false;
    private syncInterval: any = null;
    private lastProcessedData: Map<string, number> = new Map();

    constructor(
        healthService: HealthService,
        challengeService: ChallengeService,
        firebaseService: FirebaseService,
        config: Partial<WatcherConfig> = {}
    ) {
        this.healthService = healthService;
        this.challengeService = challengeService;
        this.firebaseService = firebaseService;

        this.config = {
            enabled: true,
            syncInterval: 15,
            autoUpdateChallenges: true,
            notificationReminders: true,
            reminderFrequency: 'twice_daily',
            reminderTimes: ['09:00', '18:00'],
            ...config
        };

        this.status = {
            isActive: false,
            lastSync: null,
            lastHealthUpdate: null,
            activeChallenges: 0,
            pendingUpdates: 0,
            errors: []
        };
    }

    /**
     * Start watching for health data changes
     */
    async startWatching(): Promise<ApiResponse<void>> {
        try {
            if (this.isWatching) {
                return { success: true };
            }

            console.log('🔧 HealthDataWatcher: Starting health data monitoring...');

            // Initialize health service
            const healthInit = await this.healthService.initialize();
            if (!healthInit.success) {
                throw new Error(`Failed to initialize health service: ${healthInit.error}`);
            }

            // Start periodic sync
            this.startPeriodicSync();

            // Get initial active challenges count
            await this.updateActiveChallengesCount();

            this.isWatching = true;
            this.status.isActive = true;
            this.status.lastSync = new Date();

            console.log('✅ HealthDataWatcher: Started successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthDataWatcher: Failed to start:', errorMsg);
            this.status.errors.push(errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Stop watching for health data changes
     */
    async stopWatching(): Promise<ApiResponse<void>> {
        try {
            if (!this.isWatching) {
                return { success: true };
            }

            console.log('🔧 HealthDataWatcher: Stopping health data monitoring...');

            // Stop periodic sync
            this.stopPeriodicSync();

            this.isWatching = false;
            this.status.isActive = false;

            console.log('✅ HealthDataWatcher: Stopped successfully');
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthDataWatcher: Failed to stop:', errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Process health data change and auto-update challenges
     */
    async processHealthDataChange(change: HealthDataChange): Promise<ApiResponse<void>> {
        try {
            console.log('🔧 HealthDataWatcher: Processing health data change:', change);

            // Check if this is a significant change
            const lastValue = this.lastProcessedData.get(change.type) || 0;
            const changeThreshold = this.getChangeThreshold(change.type);

            if (Math.abs(change.value - lastValue) < changeThreshold) {
                console.log('🔧 HealthDataWatcher: Change below threshold, skipping');
                return { success: true };
            }

            // Update last processed value
            this.lastProcessedData.set(change.type, change.value);

            if (this.config.autoUpdateChallenges) {
                await this.autoUpdateChallenges(change);
            }

            this.status.lastHealthUpdate = new Date();
            return { success: true };

        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error('❌ HealthDataWatcher: Failed to process health data change:', errorMsg);
            this.status.errors.push(errorMsg);
            return { success: false, error: errorMsg };
        }
    }

    /**
     * Auto-update challenges based on health data
     */
    private async autoUpdateChallenges(change: HealthDataChange): Promise<void> {
        try {
            // Get active challenges for the user
            const activeChallenges = await this.challengeService.getActiveChallenges(change.userId);
            if (!activeChallenges || activeChallenges.length === 0) {
                console.log('🔧 HealthDataWatcher: No active challenges found');
                return;
            }

            console.log(`🔧 HealthDataWatcher: Found ${activeChallenges.length} active challenges`);

            // Process each active challenge
            for (const challenge of activeChallenges) {
                // Check if challenge metric matches the health data type
                if (this.shouldUpdateChallenge(challenge, change)) {
                    console.log(`🔧 HealthDataWatcher: Auto-updating challenge ${challenge.id} with ${change.value} ${change.type}`);

                    // Assuming updateProgress returns an ApiResponse<void>
                    const updateResult = await this.challengeService.updateProgress(change.userId, challenge.id, change.value);
                    if (updateResult.success) {
                        console.log(`✅ HealthDataWatcher: Challenge ${challenge.id} updated successfully`);
                    } else {
                        console.warn(`⚠️ HealthDataWatcher: Challenge ${challenge.id} update failed: ${updateResult.error}`);
                    }
                }
            }

        } catch (error) {
            console.error('❌ HealthDataWatcher: Failed to auto-update challenges:', error);
            throw error;
        }
    }

    /**
     * Determine if a challenge should be updated based on health data
     */
    private shouldUpdateChallenge(challenge: Challenge, change: HealthDataChange): boolean {
        // Map health data types to challenge metrics
        const metricMapping: Record<string, string[]> = {
            steps: ['steps', 'step_count'],
            distance: ['distance', 'walking_distance', 'running_distance'],
            calories: ['calories', 'energy_burned'],
            activeMinutes: ['time', 'active_minutes', 'exercise_time']
        };

        const relevantMetrics = metricMapping[change.type] || [];
        return relevantMetrics.includes(challenge.metric);
    }

    /**
     * Get change threshold for different health data types
     */
    private getChangeThreshold(type: string): number {
        const thresholds: Record<string, number> = {
            steps: 100,        // 100 steps
            distance: 50,      // 50 meters
            calories: 10,      // 10 calories
            activeMinutes: 1   // 1 minute
        };

        return thresholds[type] || 0;
    }

    /**
     * Start periodic health data sync
     */
    private startPeriodicSync(): void {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }

        this.syncInterval = setInterval(async () => {
            await this.performPeriodicSync();
        }, this.config.syncInterval * 60 * 1000); // Convert minutes to milliseconds

        console.log(`🔧 HealthDataWatcher: Started periodic sync every ${this.config.syncInterval} minutes`);
    }

    /**
     * Stop periodic health data sync
     */
    private stopPeriodicSync(): void {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
            console.log('🔧 HealthDataWatcher: Stopped periodic sync');
        }
    }

    /**
     * Perform periodic health data sync
     */
    private async performPeriodicSync(): Promise<void> {
        try {
            console.log('🔧 HealthDataWatcher: Performing periodic sync...');

            // This would be called by platform-specific code
            // to trigger a health data refresh

            this.status.lastSync = new Date();
            console.log('✅ HealthDataWatcher: Periodic sync completed');

        } catch (error) {
            console.error('❌ HealthDataWatcher: Periodic sync failed:', error);
            this.status.errors.push(error instanceof Error ? error.message : 'Unknown error');
        }
    }

    /**
     * Update active challenges count
     */
    private async updateActiveChallengesCount(): Promise<void> {
        try {
            // This would get the count from the challenge service
            // For now, we'll set a placeholder
            this.status.activeChallenges = 0;
        } catch (error) {
            console.error('❌ HealthDataWatcher: Failed to update active challenges count:', error);
        }
    }

    /**
     * Get current watcher status
     */
    getStatus(): WatcherStatus {
        return { ...this.status };
    }

    /**
     * Update watcher configuration
     */
    updateConfig(newConfig: Partial<WatcherConfig>): void {
        this.config = { ...this.config, ...newConfig };
        console.log('🔧 HealthDataWatcher: Configuration updated:', this.config);
    }

    /**
     * Get current configuration
     */
    getConfig(): WatcherConfig {
        return { ...this.config };
    }
}
